<template>
  <v-dialog
    persistent
    :scrollable="true"
    v-model="showReportDialog"
    max-width="95%"
    @keydown.esc="close"
    :dark="compColor.isInDarkMode"
    :color="compColor.primeColor"
    overlay-opacity="0.9"
  >
    <div class="elevation-10 layout-column" style="border-radius: 6px;"
         :style="{background: compColor.contentFrame}">

      <!-- HEADER -->

      <div class="text-h6 font-weight-light pa-3 layout-row layout-align-center-center grey--text" :class="[isDarkMode ? 'text--lighten-4' : 'text--darken-2']">
        <strong>{{ $t('COMPONENT_REPORT.HEADER_DIALOG') }}</strong>
      </div>

      <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

      <!-- START: Ladeanimation, rotierende Kreise, ausgeführt wenn isLoading === true -->

      <div v-if="isLoading" class="layout-column layout-align-center-center"
           :style="{'background': compColor.isInDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.6)'}"
           style="border-radius:5px;position: absolute;left:0;right:0;top:0;bottom:0;backdrop-filter:blur(5px);z-index:9999999">
        <v-progress-circular
          :size="Math.round(9.175 * fontSize)"
          :width="Math.round(0.125 * fontSize)"
          rotate="90"
          :color="compColor.primeHighlightColor"
          indeterminate
          value="10"
        >
          <v-progress-circular
            :size="Math.round(8 * fontSize)"
            :width="Math.round(0.625 * fontSize)"
            :color="compColor.primeHighlightColor"
            indeterminate
            value="10"
          >
            <div class="layout-column layout-align-center-center white--text" style="height:100%;">
              <div class="layout-column layout-align-center-center" style="height: 30px;">
                <v-icon :size="Math.round(2.0 * fontSize)" color="#ffffff22">mdi-dots-horizontal</v-icon>
              </div>
              <div class="layout-column layout-align-center-center">
              <span class="ma-0 mb-n2 pa-0" style="font-weight: 100;"
                    :style="{'font-size': Math.round(0.86 * fontSize) + 'px'}">{{ $t('COMPONENT_REPORT.LOADING_ANIMATION_TEXT_PART1') }}</span>
                <h1 class="ma-0 mt-n1 mb-n1 pa-0" :style="{'font-size': Math.round(1.3 * fontSize) + 'px'}">{{ $t('COMPONENT_REPORT.LOADING_ANIMATION_TEXT_PART2') }}</h1>
              </div>
              <div class="layout-column layout-align-center-center" style="height: 30px;">
                <v-icon :size="Math.round(2.0 * fontSize)" color="#ffffff22">mdi-dots-horizontal</v-icon>
              </div>
            </div>
          </v-progress-circular>
        </v-progress-circular>
      </div>

      <!-- END: Ladeanimation, rotierende Kreise, ausgeführt wenn isLoading === true -->

      <div class="flex layout-column" style="overflow: hidden;">

        <!-- Grunddaten -->

        <div class="elevation-3 layout-column ma-2 mb-1" style="border-radius: 5px;"
             :style="{'background': compColor.lxExpansion.background}">

          <div class="layout-row pa-2">
            <div class="layout-align-start-center layout-column">
              <div style="border-radius:2px;width:40px;height:40px;"
                   :style="{'background': compColor.isInDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}"
                   class="mr-2 layout-column layout-align-center-center">
                <v-icon :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                        style="margin:0;">mdi-account-edit
                </v-icon>
              </div>
            </div>

            <div class="flex layout-column layout-align-center-start blue-grey--text text--darken-2">
              <div class="subtitle-1 font-weight-light"
                   :class="compColor.isInDarkMode ? compColor.primeHighlightClass : compColor.primeClass"
                   style="lineHeight:15px">
                <div class="layout-row layout-align-start-center">
                  <span class="mr-3">{{ $t('COMPONENT_REPORT.HEADER_BASE_DATA_REPORTER') }}</span>
                </div>
              </div>

              <div class="caption font-weight-light" :class="compColor.widget.subHeadline">
                {{ $t('COMPONENT_REPORT.SUB_LINE_BASE_DATA_REPORTER') }}
              </div>
            </div>

            <div class="pa-2 ml-4 report layout-row layout-align-start-center"
                 v-if="userIsPhysician"
                 style="border:1px solid;border-radius: 9px;"
                 :class="[!compColor.isInDarkMode ? compColor.prime + ' lighten-5 white--text ' : '']"
                 :style="{'border-color': compColor.isInDarkMode ? compColor.primeHighlightColor : 'transparent'}">
              <div class="layout-column layout-align-center-center mr-3">
                <v-icon size="20" :color="compColor.prime">mdi-information</v-icon>
              </div>

              <span style="font-size: 14px;" :class="compColor.primeClass">{{ $t('COMPONENT_REPORT.HEADER_INFO_BASE_DATA_REPORTER') }}</span>
            </div>
          </div>

          <v-divider></v-divider>

          <div>
            <div class="layout-row" v-if="isAddressInvalid || isBankDataInvalid || isReportDataInvalid">
              <lx-alert-box color="orange" class="flex mx-4 mt-2">
                <div class="layout-column">
                  <div class="layout-row">
                    <span>{{ $t('COMPONENT_REPORT.MESSAGE_MISSING_BASE_DATA_PART1') }}</span>&nbsp;
                    <span v-if="userIsPhysician"> {{ $t('COMPONENT_REPORT.MESSAGE_MISSING_BASE_DATA_PART2') }}:</span>
                  </div>

                  <span v-if="isAddressInvalid" class="pl-5">{{ $t('COMPONENT_REPORT.ADDRESS_DATA') }}</span>
                  <span v-if="isBankDataInvalid" class="pl-5">{{ $t('COMPONENT_REPORT.BANK_DATA') }}</span>
                  <span v-if="isReportDataInvalid" class="pl-5">{{ $t('COMPONENT_REPORT.REPORT_DATA') }}</span>

                  <div v-if="userIsPhysician">
                    <lx-btn classes="mr-1 mt-4 text-none"
                            :color="compColor.prime"
                            :dark="isDarkMode"
                            width="200"
                            height="30"
                            @click="routeToAccount"
                            :label="$t('COMPONENT_REPORT.BUTTON.ROUTE_TO_ACCOUNT')"
                            :tooltip="$t('COMPONENT_REPORT.BUTTON.ROUTE_TO_ACCOUNT_HINT')"></lx-btn>
                  </div>
                </div>
              </lx-alert-box>
            </div>

            <div class="pa-3 layout-row layout-wrap">

              <!-- Melder ID -->

              <div class="flex-gt-sm-50 flex-100 pr-2">
                <v-text-field
                  :label="$t('COMPONENT_REPORT.REPORTER_ID')"
                  :color="compColor.primeColor"
                  :dark="compColor.isInDarkMode"
                  v-model="user.cancer_form_details.melder_id"
                  disabled
                  class="mt-2"
                  hide-details
                  dense
                  outlined
                ></v-text-field>
              </div>

              <!-- Krebsregister -->

              <div class="flex-gt-sm-50 flex-100 pl-2">
                <v-text-field
                  :label="$t('COMPONENT_CANCER_MESSAGE_DETAILS.CANCER_REGISTER')"
                  :color="compColor.primeColor"
                  :dark="compColor.isInDarkMode"
                  v-model="cancerRegister.name"
                  disabled
                  hide-details
                  class="mt-2"
                  dense
                  outlined
                ></v-text-field>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Validation Error XML -->

        <div v-if="formValidationErrors && formValidationErrors.length > 0" class="elevation-3 layout-column ma-2 pb-2 red--text text--accent-2" style="border-radius: 5px;"
             :style="{'background': compColor.lxExpansion.background}">

          <!-- Header -->

          <div class="layout-row pa-2">
            <div class="layout-align-start-center layout-column">
              <div style="border-radius:2px;width:40px;height:40px;"
                   :style="{'background': compColor.isInDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}"
                   class="mr-2 layout-column layout-align-center-center">
                <v-icon :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                        style="margin:0;">mdi-alert
                </v-icon>
              </div>
            </div>

            <div class="flex layout-column layout-align-center-start blue-grey--text text--darken-2">
              <div class="subtitle-1 font-weight-light"
                   :class="compColor.isInDarkMode ? compColor.primeHighlightClass : compColor.primeClass"
                   style="lineHeight:15px">
                <div class="layout-row layout-align-start-center red--text text--accent-2">
                  <span class="mr-3">{{ $t('COMPONENT_REPORT.FORM_VALIDATION_ERRORS') }}</span>
                </div>
              </div>
              <div class="caption font-weight-light" :class="compColor.widget.subHeadline">
                {{ $t('COMPONENT_REPORT.FORM_VALIDATION_ERRORS_SUB_LINE') }}
              </div>
            </div>

            <!-- Copy-Button -->

            <div class="layout-column px-3 py-0 layout-align-center-start">
              <div class="body-2 layout-row" @click="copyXMLErrorsToClipboard(formValidationErrors, $event)" style="cursor: pointer;">
                <v-icon size="13" :color="compColor.prime">mdi-id-card</v-icon>
                <span class="ml-2 mr-1"
                      style="min-width:45px;line-height:23px;display: inline-block;">{{ $t('COMPONENT_REPORT.FORM_VALIDATION_ERRORS_TEXT_COPY') }}</span>
                <small style="opacity:0.5;line-height:23px;min-width: 40px;display: inline-block;">({{ $t('COMPONENT_REPORT.FORM_VALIDATION_ERRORS_LABEL_COPY') }})</small>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <!-- Content -->

          <div class="layout-column flex pt-2" style="max-height:200px;overflow-y:auto;">
            <div :key="index" v-for="(item, index) in formValidationErrors"
                 class="layout-row layout-wrap pa-0 mx-3 my-1"
                 :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
                 style="border-radius:4px;"
                 :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
              <div class="flex-gt-sm-100 flex-100 pa-4"><span style="font-size: 13px;">{{ item }}</span></div>
            </div>
          </div>
        </div>

        <!-- Form Validation Error Pre-Validation -->

        <div v-if="preValidationErrors && preValidationErrors.length > 0" class="elevation-3 layout-column flex ma-2 pb-2 red--text text--accent-2" style="border-radius: 5px;"
             :style="{'background': compColor.lxExpansion.background}">

          <!-- Header -->

          <div class="layout-row pa-2">
            <div class="layout-align-start-center layout-column">
              <div style="border-radius:2px;width:40px;height:40px;"
                   :style="{'background': compColor.isInDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}"
                   class="mr-2 layout-column layout-align-center-center">
                <v-icon :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                        style="margin:0;">mdi-alert
                </v-icon>
              </div>
            </div>

            <div class="flex layout-column layout-align-center-start blue-grey--text text--darken-2">
              <div class="subtitle-1 font-weight-light"
                   :class="compColor.isInDarkMode ? compColor.primeHighlightClass : compColor.primeClass"
                   style="lineHeight:15px">
                <div class="layout-row layout-align-start-center red--text text--accent-2">
                  <span class="mr-3">{{ $t('COMPONENT_REPORT.PRE_VALIDATION_ERRORS') }}</span>
                </div>
              </div>
              <div class="caption font-weight-light" :class="compColor.widget.subHeadline">
                {{ $t('COMPONENT_REPORT.PRE_VALIDATION_ERRORS_SUB_LINE') }}
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <!-- Content -->

          <div class="layout-column pt-2 elevation-3" style="overflow-y:auto;">
            <div :key="index" v-for="(item, index) in preValidationErrors"
                 class="layout-row layout-wrap pa-0 mx-3 my-1"
                 :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
                 style="border-radius:4px;"
                 :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
              <div class="flex-gt-sm-100 flex-100 pa-4">
                <h4>{{ item.patient.lastname }}, {{ item.patient.firstname }} (ID: {{ item.patient.ais_id }})</h4>

                <v-divider class="my-1"></v-divider>

                <div v-if="item.patientErrors && item.patientErrors.length > 0" class="layout-row flex-gt-sm-100 flex-100 my-2">
                  <span class="flex-gt-sm-20 flex-100 grey--text" :class="[isDarkMode ? 'text--lighten-4' : 'text--darken-2']">{{ $t('COMPONENT_PATIENT.BASE_DATA_MAIN_LINE') }}:</span>

                  <div class="layout-row flex-gt-sm-80 flex-100 mb-1">
                    <v-btn depressed style="max-width:180px;max-height:33px;min-height:33px;" :color="compColor.prime" :dark="isDarkMode" class="white--text text-none mr-1"
                           @click="routeToPatientBaseData(item.patient)" :disabled="isLoading">
                      {{ $t('COMPONENT_REPORT.PRE_VALIDATION_BTN_EDIT_BASE_DATA') }}
                    </v-btn>
                  </div>
                </div>

                <div v-if="item.forms && item.forms.length > 0" class="layout-row flex-gt-sm-100 flex-100 my-2">
                  <span class="flex-gt-sm-20 flex-100 grey--text" :class="[isDarkMode ? 'text--lighten-4' : 'text--darken-2']">{{ $t('COMPONENT_REPORT.FORMS') }}:</span>

                  <div class="layout-column flex-gt-sm-80 flex-100">
                    <div :key="formsErrorIndex" v-for="(formsError, formsErrorIndex) in item.forms" class="layout-row flex-gt-sm-100 flex-100">
                      <div v-if="!formsError.form.hide" class="layout-row">
                        <v-btn depressed style="max-width:180px;min-width:180px;max-height:30px;min-height:30px;" :color="compColor.prime" :dark="isDarkMode" class="flex-gt-sm-20 white--text text-none mr-1"
                               @click="routeToPatientForm(item.patient, formsError.form)" :disabled="isLoading">
                          {{ $t('COMPONENT_REPORT.PRE_VALIDATION_BTN_EDIT_FORM') }}
                        </v-btn>

                        <p class="flex flex-100 ml-2 pt-1 grey--text" :class="[isDarkMode ? 'text--lighten-4' : 'text--darken-2']" v-if="$te('forms.' + formsError.form.key)">
                          {{ $t('forms.' + formsError.form.key) }} ({{ formsError.form.formDatum | formatDate('DD.MM.YYYY') }}, {{ formsError.form.subj_id }})
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
             </div>
            </div>
          </div>
        </div>

        <!-- Meldungen -->

        <div v-if="!preValidationErrors || (preValidationErrors && preValidationErrors.length === 0)"
            class="flex elevation-3 layout-column ma-2" style="border-radius: 5px;"
             :style="{'background': compColor.lxExpansion.background}">

          <div class="layout-row pa-2">
            <div class="layout-align-start-center layout-column">
              <div style="border-radius:2px;width:40px;height:40px;"
                   :style="{'background': compColor.isInDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}"
                   class="mr-2 layout-column layout-align-center-center">
                <v-icon :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                        style="margin:0;">mdi-page-next
                </v-icon>
              </div>
            </div>

            <div class="flex layout-column layout-align-center-start blue-grey--text text--darken-2">
              <div class="subtitle-1 font-weight-light"
                   :class="compColor.isInDarkMode ? compColor.primeHighlightClass : compColor.primeClass"
                   style="lineHeight:15px">
                <div class="layout-row layout-align-start-center">
                  <span class="mr-3">{{ $t('COMPONENT_REPORT.HEADER_REPORTS') }}</span>
                </div>
              </div>
              <div class="caption font-weight-light" :class="compColor.widget.subHeadline">
                {{ allForms ? $t('COMPONENT_REPORT.SUB_LINE_REPORTS_ALL_FORMS') : $t('COMPONENT_REPORT.SUB_LINE_REPORTS_INVALID_FORMS') }}
              </div>
            </div>

            <div class="mx-5" v-if="allForms">
              <v-checkbox :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          class="my-0 py-0 label--color"
                          height="40"
                          hide-details
                          :disabled="isAccountInvalid"
                          @change="triggerChangeOfAllPatientsAreSelected(allPatientsAreSelected)"
                          v-model="allPatientsAreSelected">
                <template v-slot:label>
                  <div style="color: rgb(144, 164, 174);font-size: 14px;">{{ $t('COMPONENT_REPORT.REPORT_ALL_PATIENTS_ALL_FORMS') }}</div>
                </template>
              </v-checkbox>
            </div>

            <v-switch
              v-model="allForms"
              class="mr-2 my-0 py-0"
              dense
              inset
              :color="compColor.prime"
              :dark="isDarkMode"
              :disabled="isAccountInvalid"
              @change="toggleAllForms"
              hide-details
            >
              <template #label>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs"
                          class="body-2 blue-grey--text text--lighten-2"
                          v-on="on">{{ $t('COMPONENT_REPORT.LABEL_SWITCH_SHOW_ALL_FORMS') }}</span>
                  </template>
                  {{ $t('COMPONENT_REPORT.TOOL_TIP_SWITCH_SHOW_ALL_FORMS') }}
                </v-tooltip>
              </template>
            </v-switch>
          </div>

          <v-divider></v-divider>

          <!-- Alerts für invalide Meldungen -->

          <div class="pa-2" v-if="numberOfIncompleteReports > 0 || !isMedicalCommunity && numberOfNoBagReporters > 0 || isMedicalCommunity && numberOfDeletedReporters > 0">
            <!-- Alert für invalide Meldedaten (Meldeanlass, Meldebegründung, Meldedatum) -->

            <lx-alert-box color="red" class="flex" v-if="numberOfIncompleteReports > 0">
              <span v-if="numberOfIncompleteReports === 1">
                {{ $t('COMPONENT_REPORT.MESSAGE_ONE_INCOMPLETE_REPORTS') }}&nbsp;{{ $t('COMPONENT_REPORT.MESSAGE_REPLACING_INCOMPLETE_REPORTS') }}</span>
              <span v-if="numberOfIncompleteReports > 1">
                {{ $t('COMPONENT_REPORT.MESSAGE_COUNT_INCOMPLETE_REPORTS', { count: numberOfIncompleteReports }) }}&nbsp;{{ $t('COMPONENT_REPORT.MESSAGE_REPLACING_INCOMPLETE_REPORTS') }}</span>
            </lx-alert-box>

            <!-- Alert wenn keine BAG und Melder eines Formulars ist nicht der eingeloggte Arzt -->

            <lx-alert-box color="orange" class="flex mt-2" v-if="!isMedicalCommunity && numberOfNoBagReporters > 0">
              <span v-if="numberOfNoBagReporters === 1">
                {{ $t('COMPONENT_REPORT.MESSAGE_DIFFERENT_REPORTER_ONE_COUNT') }}&nbsp;{{ $t('COMPONENT_REPORT.MESSAGE_REPLACING_DELETED_REPORTER') }}</span>
              <span v-if="numberOfNoBagReporters > 1">
                {{ $t('COMPONENT_REPORT.MESSAGE_DIFFERENT_REPORTER_COUNT', { count: numberOfNoBagReporters }) }}&nbsp;{{ $t('COMPONENT_REPORT.MESSAGE_REPLACING_DELETED_REPORTER') }}</span>
            </lx-alert-box>

            <!-- Alert wenn BAG und Melder eines Formulars ist nicht Teil der BAG -->

            <lx-alert-box color="orange" class="flex mt-2" v-if="isMedicalCommunity && numberOfDeletedReporters > 0">
              <span v-if="numberOfDeletedReporters === 1">
                {{ $t('COMPONENT_REPORT.MESSAGE_DELETED_REPORTER_ONE_COUNT') }}&nbsp;{{ $t('COMPONENT_REPORT.MESSAGE_REPLACING_DELETED_REPORTER') }}</span>
              <span v-if="numberOfDeletedReporters > 1">
                {{$t('COMPONENT_REPORT.MESSAGE_DELETED_REPORTER_COUNT', { count: numberOfDeletedReporters }) }}&nbsp;{{ $t('COMPONENT_REPORT.MESSAGE_REPLACING_DELETED_REPORTER') }}</span>
            </lx-alert-box>

          </div>

          <v-divider v-if="numberOfIncompleteReports > 0 || !isMedicalCommunity && numberOfNoBagReporters > 0 || isMedicalCommunity && numberOfDeletedReporters > 0"></v-divider>

          <div class="flex layout-column ma-4 orange--text text--darken-3 layout-align-center-center" v-if="isAccountInvalid && userIsPhysician">
            {{ $t('COMPONENT_REPORT.MESSAGE_ACCOUNT_DATA_INVALID') }}
          </div>

          <div class="flex layout-column ma-4 orange--text text--darken-3 layout-align-center-center" v-if="isAccountInvalid && !userIsPhysician">
            {{ $t('COMPONENT_REPORT.MESSAGE_ACCOUNT_DATA_INVALID_AT_EMPLOYEE', { physician: physicianName }) }}
          </div>

          <div class="flex layout-column ma-4 light-green--text layout-align-center-center" v-if="!isAccountInvalid && patients.length === 0">
            {{ $t('COMPONENT_REPORT.MESSAGE_NO_INVALID_FORMS_FOUND') }}
          </div>

          <div class="flex layout-column" v-if="!isAccountInvalid && patients.length > 0">
            <ValidationObserver tag="div" ref="ReportDetailForm" v-slot="{}" class="flex layout-column">
              <virtual-list style="overflow-y: auto;"
                            class="flex"
                            :estimate-size="50"
                            :keeps="patientCountForScrolling"
                            :data-key="'patientId'"
                            :data-sources="patients"
                            :data-component="patientComponent"
                            :extra-props="{ registerModules: registerModules, user: user, cancerFormDetails: cancerFormDetails, selectedPatients, allForms, setAllPatientsAreSelected}"
              />
            </ValidationObserver>
          </div>
        </div>
      </div>

      <!-- ACTIONS -->

      <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

      <div class="layout-row layout-align-end-center pa-1">
        <v-btn depressed :color="compColor.prime" :dark="isDarkMode" class="flex white--text text-none mr-1"
               @click="saveAndCreateReport()" :disabled="sendButtonIsDisabled() || isLoading">
          {{ $t('COMPONENT_REPORT.BUTTON.CREATE_REPORT') }}
        </v-btn>
        <v-btn depressed color="grey" :dark="isDarkMode" class="white--text text-none" @click="close">
          {{ $t('COMPONENT_REPORT.BUTTON.CANCEL') }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import utils from '../../common/utils'
import lodash from 'lodash'
import PatientComponent from './shared/report_modules/PatientComponent'
import VirtualList from 'vue-virtual-scroll-list'

export default {
  name: 'Report',
  mixins: [utils],
  components: {
    VirtualList
  },
  props: {
    showReportDialog: Boolean
  },
  data () {
    return {
      patients: [],
      cancerRegister: {},
      physicianName: '',
      panel: [0, 2],
      selectedPatients: [],
      allForms: false,
      MeldebegruendungTypes: null,
      MeldeanlassTypes: null,
      registerModules: {},
      patientCountForScrolling: 6,
      patientComponent: PatientComponent,
      confirmSaveChangesDialogTitle: this.$t('CONFIRM_SAVE_CHANGES_DIALOG.HEADER'),
      confirmSaveChangesDialogMessage: this.$t('CONFIRM_SAVE_CHANGES_DIALOG.MESSAGE'),
      confirmSaveChangesDialogOpts: {
        ok: this.$t('buttons.save.value'),
        cancel: this.$t('buttons.close.value')
      },
      isLoading: false,
      allPatientsAreSelected: true,
      isAddressInvalid: null,
      isBankDataInvalid: null,
      isReportDataInvalid: null,
      preValidationErrors: null,
      formValidationErrors: null,
      fontSize: 20
    }
  },
  created () {
    this.MeldebegruendungTypes = this.formsMetaData.MeldebegruendungTypesSelect
    this.MeldeanlassTypes = this.formsMetaData.MeldeanlassTypes
  },
  methods: {
    /**
     * Open tab of invalid patient and show invalid base data
     *
     * @param patient The selected invalid patient
     */
    routeToPatientBaseData (patient) {
      // store data of patient to show base data, when opening tab
      this.$store.dispatch('setRoutingAfterReportValidation', { patientId: patient._id, showBaseData: true })

      this.addTabToStore({
        routeName: 'PatientDetails',
        routeParams: { patientId: patient._id },
        options: {
          headline: patient.lastname + ', ' + patient.firstname
        }
      })

      // Close dialog
      this.$emit('set-show-report-dialog')
    },

    /**
     * open tab of invalid patient and show selected invalid form
     *
     * @param patient The selected patient
     * @param form The selected invalid form
     */
    routeToPatientForm (patient, form) {
      const formData = {
        id: form._id,
        key: form.key,
        subj_id: form.subj_id,
        registerId: form.registerFormId,
        diagDate: form.diagnoseDatum,
        diagCode: form.primaertumorIcdCode,
        localization: form.seitenlokalisation
      }

      // store patient and form information to get possibility to route to selected invalid form
      this.$store.dispatch('setRoutingAfterReportValidation', { patientId: patient._id, showFormData: true, formDetails: formData })

      this.addTabToStore({
        routeName: 'PatientDetails',
        routeParams: { patientId: patient._id },
        options: {
          headline: patient.lastname + ', ' + patient.firstname
        }
      })

      // Close dialog
      this.$emit('set-show-report-dialog')
    },

    /**
     * Copy selected form validation errors to clipboard
     *
     * @param errors The form validation errors in xml
     * @param event The event, is necessary to know position of mouse-click
     */
    copyXMLErrorsToClipboard (errors, event) {
      let errorsText = ''

      errors.forEach(error => errorsText += error + '\n')

      // create textarea with text content to copy to clipboard
      let textArea = document.createElement('textarea')
      textArea.addEventListener('focusin', e => e.stopPropagation()) // must set, because of v-dialog has focus
      textArea.value = errorsText
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      textArea.remove()

      // show little tooltip to user, that ais-id is copied
      let copied = document.createElement('div')
      copied.setAttribute('class', 'copied_xml_errors')
      copied.setAttribute('style', 'left: ' + (event.pageX + 10) + 'px; top: ' + (event.pageY + 20) + 'px; background: ' + this.compColor.primeColor)
      copied.appendChild(document.createTextNode( this.$t('COMPONENT_REPORT.FORM_VALIDATION_ERRORS_HINT_COPY')))
      document.body.appendChild(copied)
      setTimeout(() => {
        document.body.removeChild(copied)
      }, 1000)
    },

    /**
     * Toggle function for switch of value allForms, save all manually changed forms if necessary
     */
    toggleAllForms () {
      const formsToUpdate = this.getFormsWithChanges()

      if (formsToUpdate.length === 0) {
        this.getData()
        return
      }

      this.$http.put('/report/save', formsToUpdate)
        .then(() => {
          this.getData()
        })
        .catch(error => {
          this.allForms = !this.allForms
          this.NotifyError(error)
        })
    },

    /**
     * Helper function to return all forms with manually changes
     */
    getFormsWithChanges () {
      // filter forms which are modified ('bearbeiten' button was tapped -> isEditable set to true)
      let formsToUpdate = []
      this.patients?.forEach(patient => {
        patient.diagnoses.forEach(diagnosis => {
          formsToUpdate.push(...diagnosis.forms.filter(form => form.isEditable))
        })
      })
      return formsToUpdate
    },

    /**
     * Set value of checkbox of AllPatientsAreSelected after changing selection state of one patient
     */
    setAllPatientsAreSelected () {
      this.allPatientsAreSelected = this.patients.length === this.selectedPatients.length
    },

    /**
     * Helper method to set all patients as selected
     */
    setSelectedFlagToAllPatients () {
      if (this.allPatientsAreSelected) {
        this.patients.forEach(patient => {
          patient.isSelected = true
          this.selectedPatients.push(patient.patientId)
        })
      }
    },

    /**
     * Select or deselect all patients after clicking checkbox of allPatientsAreSelected
     */
    triggerChangeOfAllPatientsAreSelected (newValue) {
      this.selectedPatients = []

      if (newValue) {
        this.setSelectedFlagToAllPatients()
      } else {
        this.patients.forEach(patient => {
          delete patient.isSelected
        })
      }
    },

    /**
     * Get all patients with all forms, which can be sent in dependency to value of allForms
     */
    getData () {
      if (this.isLoading) return
      this.isLoading = true

      this.$http.get('/report/getForms?complete=' + this.allForms.toString())
        .then(res => {
          if (res.data) {
            // set error flags
            if (res.data.errors) {
              this.isAddressInvalid = res.data.errors.addressInvalid || false
              this.isBankDataInvalid = res.data.errors.bankDataInvalid || false
              this.isReportDataInvalid = res.data.errors.reportDataInvalid || false
              this.preValidationErrors = res.data.errors.validationErrors || null
            }

            // loop though each patient in pre-validation-errors to get errors with missing tumor information, if exists
            this.preValidationErrors.forEach((error) => {
              let errorDiagnosisForm = []

              // loop through form errors, if exist
              if (error.forms && error.forms.length > 0) {
                error.forms.forEach(form => {

                  // get errors with missing primary tumor information
                  const errorsWithDiagnosis = form.errors.filter(err => {
                    let regex = RegExp(/primaertumor/g)
                    return regex.test(err.description)
                  })

                  // if errors in form ar greater or equal to errors with tumor information, push it to error-array
                  if (errorsWithDiagnosis.length > 0) {
                    let diagnosisForm = lodash.cloneDeep(form.form)

                    diagnosisForm.key = 'register'
                    diagnosisForm._id = form.form.registerFormId
                    diagnosisForm.formDatum = form.form.diagnoseDatum

                    errorDiagnosisForm.push({form: diagnosisForm})

                    // hide form, if error with tumor information are the only one
                    if (form.errors.length === errorsWithDiagnosis.length) form.form.hide = true
                  }
                })
              }

              // add invalid register-forms to patient-errors, ensure, that register form only one time per subj_id will be added
              if (errorDiagnosisForm.length > 0) {
                errorDiagnosisForm.forEach(diagnosisError => {
                  if (Boolean(error.forms.find(form => form.form.key === 'register' && diagnosisError.form._id === form.form._id)) === false) {
                    error.forms.push(diagnosisError)
                  }
                })
              }
            })

            // reset list of selectedPatients
            this.selectedPatients = []

            // set patients per default as array to prevent errors in virtual scroll
            this.patients = res.data.patients || []
            this.cancerRegister = res.data.cancerRegister || {}
            this.registerModules = res.data.registerModules || {}
            this.physicianName = res.data.physician || ''

            // when loading all patients set all of them on selected as default
            if (this.allForms) this.setSelectedFlagToAllPatients()
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.isLoading = false
        })
    },

    /**
     * Disable send-button if at least one form, physician details or at least one reporter is invalid
     */
    sendButtonIsDisabled () {
      // disable button if error exists, form or physician details are invalid
      // disable also if BAG and reporter on at least one form is not in list of physicians of BAG
      // disable also if not BAG and reporter on at least one form is not the user who wants to report

      // get ensure, that at least on patient is selected if allForms is true
      const atLeastOnePatientIsSelected = (this.allForms && this.selectedPatients.length > 0) || !this.allForms
      const validationErrorsExist = (this.formValidationErrors && this.formValidationErrors.length > 0) || (this.preValidationErrors && this.preValidationErrors.length > 0)
      return !atLeastOnePatientIsSelected || this.numberOfIncompleteReports > 0 || this.isAccountInvalid || this.numberOfNoBagReporters > 0 || this.numberOfDeletedReporters > 0 || validationErrorsExist
    },

    /**
     * Show or hide tooltip in dependency of reporter in list of members of deactivated BAG
     */
    isReporterInList (formHeader) {
      if (formHeader && formHeader.reporter && formHeader.reporter.melderId) {
        return this.cancerFormDetails.some(physician => physician.melderId === formHeader.reporter.melderId)
      }

      return true
    },

    /**
     * Validate all selected Forms and return true if one of them is invalid
     *
     * @returns {boolean}
     */
    // isFormInvalid () {
    //   return Object.keys(this.$refs.ReportDetailForm?.fields || {}).some(key => {
    //     const keySplit = key.split('.')
    //
    //     // get form and validate it only when it is selected
    //     const patientIndex = keySplit[2]
    //     const diagnosisIndex = keySplit[4]
    //     const formIndex = keySplit[6]
    //     const form = this.patients[patientIndex]?.diagnoses[diagnosisIndex]?.forms[formIndex] || {}
    //
    //     return form && form.isSelected ? this.$refs.ReportDetailForm.fields[key].invalid : false
    //   })
    // },

    /**
     * Save all changed forms in db if necessary and create report
     */
    saveAndCreateReport () {
      if (this.isLoading) return
      this.isLoading = true

      this.formValidationErrors = null
      const formsToUpdate = this.getFormsWithChanges()

      if (formsToUpdate.length === 0) {
        this.createReport()
        return
      }

      // Save changes before creating report
      this.$http.put('/report/save', formsToUpdate)
        .then(() => {
          this.createReport()
        })
        .catch((error) => {
          this.NotifyError(error)
          this.isLoading = false
        })
    },

    /**
     * Create report-XML and open file explorer for download
     */
    createReport () {
      this.$http.post('/report/createCancerReport', { patientIds: this.selectedPatients })
        .then(res => {
          // Open file explorer directly to save xml
          this.openFileExplorerToSaveReport(res.data.xml)

          // Trigger reload of all patient tabs via eventBus
          this.$eventBus.emit('updatePatientDetail')

          // Close dialog
          this.$emit('set-show-report-dialog')
        })
        .catch(err => {
          // if validation errors return from server, show them in view
          if (err?.response?.status === 422 && err.response.data?.validationErrors && err.response.data.validationErrors.length > 0) {
            this.formValidationErrors = err.response.data.validationErrors
          } else {
            this.NotifyError(err)
          }

          // let options = {
          //   type: 'error',
          //   duration: 120000,
          //   title: this.$t('COMPONENT_REPORT.CREATE_REPORT.TITLE'),
          //   text: this.$t('COMPONENT_REPORT.CREATE_REPORT.TEXT_ERROR')
          // }
          //
          // this.NotifySystem(options)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /**
     * Close modal of report dialog and warn, if there are forms with changes
     */
    close () {
      const formsToUpdate = this.getFormsWithChanges()

      if (formsToUpdate.length === 0) {
        this.allForms = false
        this.$emit('set-show-report-dialog')
        return
      }

      this.DialogConfirm(
        this.confirmSaveChangesDialogTitle,
        this.confirmSaveChangesDialogMessage,
        this.confirmSaveChangesDialogOpts)
        .then(() => {
          this.$http.put('/report/save', formsToUpdate)
            .then(() => {
              this.allForms = false
              this.$emit('set-show-report-dialog')
            })
            .catch(this.NotifyError)
        })
        .catch(() => {
          this.allForms = false
          this.$emit('set-show-report-dialog')
        })
    },

    /**
     * Close modal and add account tab
     */
    routeToAccount () {
      // Close modal
      this.$emit('set-show-report-dialog')

      // Add account tab or jump in existing one
      this.addTabToStore({ routeName: 'AccountDetails', options: { headline: 'Account', icon: 'mdi-account-circle' } })
    }
  },
  watch: {
    'showReportDialog' (newValue) {
      if (newValue) {
        this.formValidationErrors = null
        this.preValidationErrors = null

        this.getData()
      }
    }
  },
  computed: {
    user () {
      return this.authStore.getters.getAuthUserData?.user || {}
    },
    // list of members of medical community for selection
    cancerFormDetails () {
      let physicianList = [
        {
          name: this.user.lastname + ', ' + this.user.firstname,
          melderId: this.user.cancer_form_details.melder_id,
          lanr: this.user.lanr,
          iknr: this.user.iknr
        }
      ]

      if (this.user && this.user.cancer_form_details && this.user.cancer_form_details.isMedicalCommunity && this.user.cancer_form_details.medicalCommunity && this.user.cancer_form_details.medicalCommunity.physicians) {
        return physicianList.concat(this.user.cancer_form_details.medicalCommunity.physicians)
      }

      return physicianList
    },
    // return boolean, if physician is part of BAG
    isMedicalCommunity () {
      return this.user && this.user.cancer_form_details && this.user.cancer_form_details.isMedicalCommunity
    },
    // get number of reports without report date, report reason or report cause
    numberOfIncompleteReports () {
      let count = 0

      if (Array.isArray(this.patients)) {
        this.patients.some(patient => {
          if (patient && Array.isArray(patient.diagnoses)) {
            patient.diagnoses.some(diagnosis => {
              if (diagnosis && Array.isArray(diagnosis.forms)) {
                diagnosis.forms.some(form => {
                  // patient must be selected for invalidity, when allForms is true
                  const modus = this.allForms && this.selectedPatients.indexOf(patient.patientId) > -1 || !this.allForms
                  // count form only if form is selected and header is invalid
                  if ((!form.Meldeanlass || !form.Meldebegruendung || !form.Meldedatum) && modus) {
                    count++
                  }
                })
              }
            })
          }
        })
      }

      return count
    },
    // get number of physicians, who are reporter of form but not logged in physician (BAG not exists or is deselected)
    numberOfNoBagReporters () {
      let count = 0

      if (Array.isArray(this.patients) && !this.user.cancer_form_details.isMedicalCommunity) {
        this.patients.forEach((patient) => {
          if (patient && patient.diagnoses) {
            let filterPatient = []

            patient.diagnoses.forEach((diagnosis) => {
              if (diagnosis.forms) {
                filterPatient = diagnosis.forms.filter((item) => item.isSelected && item.reportHeader && item.reportHeader.reporter && item.reportHeader.reporter.melderId && item.reportHeader.reporter.melderId !== this.user.cancer_form_details.melder_id)
                count += filterPatient.length
              }
            })
          }
        })
      }

      return count
    },
    // get number of deleted physicians, who are reporter of form(BAG exists)
    numberOfDeletedReporters () {
      let count = 0

      if (Array.isArray(this.patients)) {
        this.patients.forEach((patient) => {
          if (patient && patient.diagnoses) {
            let filterPatient = []

            patient.diagnoses.forEach((diagnosis) => {
              if (diagnosis.forms) {
                filterPatient = diagnosis.forms.filter((item) => {
                  return item.isSelected && item.reportHeader && !this.isReporterInList(item.reportHeader)
                })

                count += filterPatient.length
              }
            })
          }
        })
      }

      return count
    },
    // return true when account data are invalid and user action is required
    isAccountInvalid () {
      if (this.isAddressInvalid == null || this.isBankDataInvalid == null || this.isReportDataInvalid == null) {
        return true
      }
      return this.isAddressInvalid || this.isBankDataInvalid || this.isReportDataInvalid
    },
    userIsPhysician () {
      return this.authStore.getters.userIsPhysician
    }
  }
}
</script>

<style>
.copied_xml_errors {
  position: absolute;
  width: 150px;
  height: 30px;
  padding-top: 6px;
  text-align: center;
  color: white;
  opacity: 0.6;
  vertical-align: middle;
  border-radius: 5px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  z-index: 999;
}
</style>

